import React from "react";
import { Alert, type AlertRole } from "../ui/Alert";
import { useTranslation } from "react-i18next";

type AlertVariant = "warning" | "danger" | "info" | "success";

interface AlertBannerProps {
  /** Alert variant. Defaults to "warning". */
  variant?: AlertVariant;
  role?: AlertRole;
  /** Optional icon rendered to the left of the message. */
  icon?: React.ReactNode;
  /**
   * i18next translation key.  When provided the component calls `t()`
   * internally so callers do not need to import `useTranslation`.
   */
  translatedKey?: string;
  /** Fallback / default text when `translatedKey` is used. */
  messageDefault?: string;
  /** Interpolation values forwarded to `t()`. */
  translationParams?: Record<string, unknown>;
  /** Extra CSS class names appended to the root `<Alert>`. */
  className?: string;
  contentClassName?: string;
  /** CSS class names applied to the text content of the alert. */
  textClassName?: string;
  /**
   * Arbitrary child content.  When both `translatedKey` and `children`
   * are supplied, `children` takes precedence.
   */
  children?: React.ReactNode;
}

/**
 * A general-purpose alert banner used across booking, pre-order and
 * manage-order flows.  It wraps the custom `<Alert>` component with
 * sensible defaults and built-in i18next support.
 */
const AlertBanner: React.FC<AlertBannerProps> = ({
  variant,
  role,
  icon = null,
  translatedKey,
  messageDefault = "",
  translationParams,
  className = "",
  contentClassName = "gcw:gap-2 gcw:p-4",
  textClassName = "",
  children,
}) => {
  const { t } = useTranslation();

  const content =
    children ?? (translatedKey ? t(translatedKey, { ...translationParams, defaultValue: messageDefault }) : null);

  if (!content) return null;

  const resolvedClassName = variant ? `gcw:flex gcw:items-start ${className}`.trim() : `${className} gcw:mt-2`.trim();

  return (
    <Alert variant={variant} role={role} className={resolvedClassName}>
      <div className={`gcw:flex gcw:flex-row gcw:items-center ${contentClassName}`.trim()}>
        {icon && <div className="alert-icon">{icon}</div>}
        <span className={textClassName}>{content}</span>
      </div>
    </Alert>
  );
};

export default AlertBanner;
